import { createClient } from '@supabase/supabase-js';
import type { Session } from '@supabase/supabase-js';
import { createBoundedFetch, createSessionSource, parseAuthLink } from '@cerebro-diem/core';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabasePublishableKey) {
  throw new Error('Missing Supabase environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY must be set');
}

/** The invite or password-reset link this page was opened with, if any. */
export const initialAuthLink = parseAuthLink(window.location.href);

/** Where auth-js keeps the session. Named for the project, as supabase-js names it. */
const storageKey = `sb-${new URL(supabaseUrl).hostname.split('.')[0]}-auth-token`;

/**
 * The same deadline the app carries. A browser gives up on a dead connection
 * sooner than Android does, and pauses the refresh ticker on a hidden tab, so a
 * stuck request is less likely here - but nothing bounds it.
 */
const boundedFetch = createBoundedFetch(() => window.fetch.bind(window));

/**
 * The client that owns the session: it signs in and out, persists the session
 * and refreshes it. Nothing else on the site reaches into it.
 */
const authClient = createClient(supabaseUrl, supabasePublishableKey, {
  auth: {
    storageKey,
    // The site never signs in from its URL. Sign-in is a password, or an email
    // link the set-password page verifies (verifyOtp) after the person presses
    // Continue. A session handed over in a URL could be anyone's, crafted by anyone.
    detectSessionInUrl: false,
    // PKCE refuses hash-token redirects too. verifyOtp needs no code verifier, so
    // email links still work in whichever browser opens them.
    flowType: 'pkce',
  },
  global: { fetch: boundedFetch },
});

/**
 * CER-166. auth-js answers `getSession` from inside the same lock it holds for
 * the whole of a refresh, so asking it for a token puts the caller behind a
 * refresh that may be stuck - a wait of about a minute for a page whose token
 * was good all along. This reads the stored session first and uses it when it
 * has more than auth-js's own renewal margin left. A session that is genuinely
 * due still goes to auth-js, which refreshes it, single-flights concurrent
 * callers and reports failure.
 */
const sessionSource = createSessionSource<Session>({
  readStoredSession: () => {
    try {
      return window.localStorage.getItem(storageKey);
    } catch {
      // A browser that keeps no site data. auth-js decides from here.
      return null;
    }
  },
  getSession: () => authClient.auth.getSession(),
});

/**
 * The client every query, upload and edge-function call goes through. Its token
 * comes from the session above rather than from auth-js's lock, so one stuck
 * refresh no longer stops the page. It has no auth of its own - there is one
 * session, and `authClient` owns it.
 */
export const supabase = createClient(supabaseUrl, supabasePublishableKey, {
  accessToken: () => sessionSource.accessToken(supabasePublishableKey),
  global: { fetch: boundedFetch },
});

/**
 * The session as it stands, without queueing behind a refresh it does not need.
 * Use this everywhere a page wants the session or its access token; reaching for
 * auth-js directly reinstates the wait this exists to remove.
 */
export async function getSession(): Promise<{ data: { session: Session | null } }> {
  return { data: { session: await sessionSource.currentSession() } };
}

const clientAuth = authClient.auth;

/**
 * Signing in and out, and watching the session. Reading the session is
 * `getSession` above, which is why it is the one member not taken from auth-js.
 */
export const auth = {
  getSession,
  onAuthStateChange: clientAuth.onAuthStateChange.bind(clientAuth),
  signInWithPassword: clientAuth.signInWithPassword.bind(clientAuth),
  signOut: clientAuth.signOut.bind(clientAuth),
  resetPasswordForEmail: clientAuth.resetPasswordForEmail.bind(clientAuth),
  verifyOtp: clientAuth.verifyOtp.bind(clientAuth),
  updateUser: clientAuth.updateUser.bind(clientAuth),
  getUser: clientAuth.getUser.bind(clientAuth),
};

/**
 * The signed-in user's id, for inserts into tables whose user_id has no
 * default (notes, people, containers). Row-level security still decides
 * what is written; this only names the owner. Throws when signed out.
 */
export async function signedInUserId(): Promise<string> {
  const session = await sessionSource.currentSession();
  if (!session) throw new Error('you are signed out; sign in and try again');
  return session.user.id;
}
